// src/features/workflow/KeyValueEditor.tsx
// Editable key/value map for environment variables, masking values whose key
// looks like a secret (token, password, api_key, ...). Reveal per-row is opt-in.

import { useState } from 'react';
import { Button, Input } from '@react/components';
import { isSecretKey } from './logic';

export interface KeyValueEditorProps {
  value: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  keyLabel: string;
  valueLabel: string;
  addLabel: string;
  emptyLabel: string;
}

interface Row {
  k: string;
  v: string;
}

export function KeyValueEditor({ value, onChange, keyLabel, valueLabel, addLabel, emptyLabel }: KeyValueEditorProps) {
  const [rows, setRows] = useState<Row[]>(() =>
    Object.entries(value || {}).map(([k, v]) => ({ k, v })),
  );
  const [revealed, setRevealed] = useState<Record<number, boolean>>({});

  const commit = (next: Row[]) => {
    setRows(next);
    const map: Record<string, string> = {};
    for (const r of next) {
      if (r.k.trim()) map[r.k.trim()] = r.v;
    }
    onChange(map);
  };

  const update = (i: number, patch: Partial<Row>) => {
    commit(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };

  const remove = (i: number) => commit(rows.filter((_, idx) => idx !== i));
  const add = () => setRows([...rows, { k: '', v: '' }]);

  return (
    <div className="space-y-2">
      {rows.length === 0 && (
        <p className="text-sm text-muted-foreground">{emptyLabel}</p>
      )}
      {rows.map((row, i) => {
        const secret = isSecretKey(row.k);
        const show = revealed[i] || !secret;
        return (
          <div key={i} className="flex items-end gap-2">
            <Input
              label={i === 0 ? keyLabel : undefined}
              value={row.k}
              onChange={(e) => update(i, { k: e.target.value })}
              className="flex-1"
              placeholder="KEY"
            />
            <Input
              label={i === 0 ? valueLabel : undefined}
              type={show ? 'text' : 'password'}
              value={row.v}
              onChange={(e) => update(i, { v: e.target.value })}
              className="flex-1"
              placeholder="value"
            />
            {secret && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={show ? 'Hide value' : 'Reveal value'}
                onClick={() => setRevealed((r) => ({ ...r, [i]: !show }))}
              >
                {show ? (
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label="Remove"
              onClick={() => remove(i)}
            >
              <svg className="h-4 w-4 text-destructive" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </Button>
          </div>
        );
      })}
      <Button type="button" variant="secondary" size="sm" onClick={add}>
        {addLabel}
      </Button>
    </div>
  );
}

export default KeyValueEditor;
